import React from 'react';
import { Box, Typography } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';

/**
 * Displays development recommendations as an interactive timeline
 * @param {Array} recommendations - development_recommendations array from interview_metadata
 */
const DevelopmentRoadmap = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  const getPriorityLevel = (recommendation, index) => {
    // First 3 are high priority, rest are medium
    return index < 3 ? 'high' : 'medium';
  };

  const getPriorityColor = (priority) => {
    return 'rgba(255, 255, 255, 0.9)';
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2.5,
        backgroundColor: 'rgba(12, 14, 22, 0.82)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 14px 36px rgba(0, 0, 0, 0.35)',
        backdropFilter: 'blur(14px)',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: 'rgba(255,255,255,0.95)',
          fontWeight: 600,
          mb: 2,
          fontSize: '1.05rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Development Roadmap
      </Typography>

      <Timeline position="alternate">
        {recommendations.map((recommendation, index) => {
          const priority = getPriorityLevel(recommendation, index);
          const priorityColor = getPriorityColor(priority);

          return (
            <TimelineItem key={index}>
              <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2">
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.03em' }}>
                    {priority} Priority
                  </Typography>
                </Box>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 0 10px rgba(255, 255, 255, 0.25)',
                    width: 10,
                    height: 10,
                  }}
                />
                {index < recommendations.length - 1 && (
                  <TimelineConnector
                    sx={{
                      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.08) 100%)',
                      width: 2,
                    }}
                  />
                )}
              </TimelineSeparator>
              <TimelineContent sx={{ py: '10px', px: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.05)',
                      transform: 'translateX(4px)',
                      boxShadow: '0 6px 18px rgba(255, 255, 255, 0.12)',
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, mb: 0.5, fontSize: '0.8rem' }}>
                    Step {index + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', lineHeight: 1.5 }}>
                    {recommendation}
                  </Typography>
                </Box>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default DevelopmentRoadmap;
